import { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { Link } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';

import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  radii,
} from '../../../theme/tokens';
import {
  VantageReportNotFoundError,
  useVantageCost,
  type CostTrendPoint,
} from './useVantageCost';
import { ProviderIcon } from './providerIcons';

const VantageIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    style={{ display: 'block', flexShrink: 0 }}
    aria-hidden
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.28508 2.02236C7.24068 0.703789 9.53982 4.5488e-07 11.8918 4.5488e-07C13.4536 -0.000433521 15.0002 0.309661 16.4431 0.91256C17.8861 1.51547 19.1972 2.39936 20.3016 3.51375C21.406 4.62813 22.2819 5.95118 22.8793 7.40729C23.4768 8.86339 23.7841 10.424 23.7836 12C23.7836 14.3733 23.0862 16.6934 21.7795 18.6668C20.4728 20.6403 18.6156 22.1783 16.4426 23.0866C14.2697 23.9948 11.8786 24.2324 9.57182 23.7694C7.26505 23.3064 5.14614 22.1635 3.48304 20.4852C1.81994 18.807 0.687352 16.6689 0.228504 14.3411C-0.230345 12.0133 0.00515151 9.60053 0.905218 7.40781C1.80528 5.21509 3.32948 3.34094 5.28508 2.02236ZM3.34202 10.3716L6.90989 13.9752C6.94532 14.0123 6.98776 14.0418 7.03472 14.0619C7.08168 14.082 7.13218 14.0924 7.1832 14.0924C7.23422 14.0924 7.28474 14.082 7.3317 14.0619C7.37867 14.0418 7.42111 14.0123 7.45652 13.9752L11.0179 10.3716C11.089 10.2997 11.129 10.2023 11.129 10.1007C11.129 9.9992 11.089 9.90178 11.0179 9.82987L7.4467 6.22956C7.37549 6.15776 7.27897 6.11742 7.17831 6.11742C7.07764 6.11742 6.98109 6.15776 6.90989 6.22956L3.34202 9.82987C3.27085 9.90178 3.2309 9.9992 3.2309 10.1007C3.2309 10.2023 3.27085 10.2997 3.34202 10.3716ZM12.1603 18.7283L15.7281 15.128C15.7993 15.0562 15.8392 14.9588 15.8392 14.8572C15.8392 14.7556 15.7993 14.6581 15.7281 14.5863L12.1603 10.9827C12.0891 10.9108 11.9924 10.8705 11.8918 10.8705C11.7911 10.8705 11.6946 10.9108 11.6234 10.9827L8.05227 14.5863C7.98111 14.6581 7.94113 14.7556 7.94113 14.8572C7.94113 14.9588 7.98111 15.0562 8.05227 15.128L11.6234 18.7283C11.6946 18.8001 11.7911 18.8404 11.8918 18.8404C11.9924 18.8404 12.0891 18.8001 12.1603 18.7283ZM16.8705 13.9752L20.4383 10.3716C20.474 10.3363 20.5023 10.294 20.5216 10.2476C20.5409 10.2011 20.5508 10.1512 20.5508 10.1007C20.5508 10.0503 20.5409 10.0004 20.5216 9.95387C20.5023 9.90738 20.474 9.86525 20.4383 9.82987L16.8705 6.22956C16.7993 6.15776 16.7028 6.11742 16.602 6.11742C16.5014 6.11742 16.4049 6.15776 16.3337 6.22956L12.7625 9.82987C12.6914 9.90178 12.6514 9.9992 12.6514 10.1007C12.6514 10.2023 12.6914 10.2997 12.7625 10.3716L16.3337 13.9752C16.4049 14.047 16.5014 14.0874 16.602 14.0874C16.7028 14.0874 16.7993 14.047 16.8705 13.9752Z"
      fill="#6742D6"
    />
  </svg>
);

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDate(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

const CHART_H = 80;
const BAR_W = 8;
const BAR_GAP = 2;

function barCenterPct(i: number, n: number): number {
  const totalW = n * (BAR_W + BAR_GAP) - BAR_GAP;
  return ((i * (BAR_W + BAR_GAP) + BAR_W / 2) / totalW) * 100;
}

function DailyBarChart({
  points,
  color,
  currency,
}: {
  points: CostTrendPoint[];
  color: string;
  currency: string;
}) {
  const theme = useTheme();
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  if (points.length < 2) return null;

  const n = points.length;
  const amounts = points.map(p => p.amount);
  const max = Math.max(...amounts) || 1;
  const totalW = n * (BAR_W + BAR_GAP) - BAR_GAP;

  // ~6 evenly-spaced axis labels; always include first and last
  // Labels at the first bar, each Monday within the period, and the last bar
  const labelIndices: number[] = [0];
  for (let i = 1; i < n - 1; i++) {
    const dow = new Date(`${points[i].date}T00:00:00`).getDay();
    if (dow === 1) labelIndices.push(i); // Monday
  }
  if (labelIndices[labelIndices.length - 1] !== n - 1) labelIndices.push(n - 1);

  const hovered = hoveredIdx !== null ? points[hoveredIdx] : null;

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Hover tooltip */}
      {hovered !== null && hoveredIdx !== null && (
        <Paper
          elevation={3}
          sx={{
            pointerEvents: 'none',
            position: 'absolute',
            bottom: CHART_H + 20, // above the chart
            left: `${barCenterPct(hoveredIdx, n)}%`,
            transform: 'translateX(-50%)',
            zIndex: 10,
            px: 1.25,
            py: 0.75,
            borderRadius: 1,
            whiteSpace: 'nowrap',
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.4 }}>
            {formatDate(hovered.date)}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 800, lineHeight: 1.4 }}>
            {formatCurrency(hovered.amount, currency)}
          </Typography>
        </Paper>
      )}

      {/* Bars — preserveAspectRatio="none" stretches to fill container */}
      <svg
        viewBox={`0 0 ${totalW} ${CHART_H}`}
        width="100%"
        height={CHART_H}
        preserveAspectRatio="none"
        style={{ display: 'block' }}
        onMouseLeave={() => setHoveredIdx(null)}
      >
        {points.map((p, i) => {
          const barH = Math.max(1, (p.amount / max) * CHART_H);
          const isHovered = i === hoveredIdx;
          return (
            <rect
              key={p.date}
              x={i * (BAR_W + BAR_GAP)}
              y={CHART_H - barH}
              width={BAR_W}
              height={barH}
              fill={color}
              fillOpacity={isHovered ? 1 : 0.7}
              rx={1}
              onMouseEnter={() => setHoveredIdx(i)}
              style={{ cursor: 'crosshair' }}
            />
          );
        })}
      </svg>

      {/* X-axis date labels, centered on their corresponding bar */}
      <Box sx={{ mt: 0.75, position: 'relative', height: 16 }}>
        {labelIndices.map(i => (
          <Typography
            key={points[i].date}
            sx={{
              color: theme.palette.text.disabled,
              fontSize: 10,
              lineHeight: 1,
              position: 'absolute',
              left: `${barCenterPct(i, n)}%`,
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
            }}
          >
            {formatDate(points[i].date)}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

export const EntityCostReportPanel = () => {
  const { entity } = useEntity();
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const accentColor = theme.palette.primary.main;

  const { loading, error, data } = useVantageCost(entity, 30);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  if (error || !data) {
    const isNotFound = error instanceof VantageReportNotFoundError;
    return (
      <Box
        sx={{
          backgroundColor: getSurfaceColor(theme, 'raised'),
          border: `1px solid ${getSurfaceBorder(theme)}`,
          borderRadius: `${radii.xl}px`,
          p: 3,
        }}
      >
        <Typography
          sx={{
            color: tokens.textPrimary,
            fontSize: 14,
            fontWeight: 700,
            mb: 0.75,
          }}
        >
          {isNotFound ? 'Vantage report not found' : 'Could not load cost data'}
        </Typography>
        <Typography sx={{ color: tokens.textMuted, fontSize: 14 }}>
          {isNotFound ? (
            <>
              The report token on this entity does not exist in Vantage. Update
              the <code>vantage.sh/report-token</code> annotation with a valid
              token and re-catalog the entity.
            </>
          ) : (
            'An error occurred while fetching cloud cost data. Try refreshing the page.'
          )}
        </Typography>
      </Box>
    );
  }

  const {
    total,
    previousTotal,
    periodStart,
    periodEnd,
    trend,
    breakdown,
    breakdownColumns,
    reportTitle,
    reportUrl,
  } = data;

  const windowDays =
    Math.round(
      (new Date(periodEnd).getTime() - new Date(periodStart).getTime()) /
        86_400_000,
    ) + 1;

  const delta =
    previousTotal !== undefined ? total.amount - previousTotal : undefined;
  const deltaPct =
    delta !== undefined && previousTotal
      ? Math.round((delta / previousTotal) * 100)
      : undefined;
  const deltaIsUp = delta !== undefined && delta > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header summary */}
      <Box
        sx={{
          backgroundColor: getSurfaceColor(theme, 'raised'),
          border: `1px solid ${getSurfaceBorder(theme)}`,
          borderRadius: `${radii.xl}px`,
          p: 3,
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Typography
            sx={{ color: tokens.textPrimary, fontSize: 14, fontWeight: 700 }}
          >
            {reportTitle}
          </Typography>
          <Link to={reportUrl} target="_blank" rel="noopener noreferrer">
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
              <VantageIcon size={14} />
              <Typography
                sx={{ color: accentColor, fontSize: 12, fontWeight: 700 }}
              >
                Open in Vantage ↗
              </Typography>
            </Box>
          </Link>
        </Box>

        <Typography
          sx={{
            color: tokens.textMuted,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.06em',
            mb: 1,
            textTransform: 'uppercase',
          }}
        >
          Last {windowDays} days · {periodStart} – {periodEnd}
        </Typography>

        <Box
          sx={{
            alignItems: 'flex-end',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography
            sx={{
              color: tokens.textPrimary,
              fontSize: 40,
              fontWeight: 800,
              letterSpacing: '-0.03em',
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            {formatCurrency(total.amount, total.currency)}
          </Typography>

          {delta !== undefined && deltaPct !== undefined && (
            <Typography
              sx={{
                color: deltaIsUp
                  ? theme.palette.error.main
                  : theme.palette.success.main,
                fontSize: 14,
                fontWeight: 700,
                lineHeight: 1.2,
                textAlign: 'right',
              }}
            >
              {deltaIsUp ? '▲' : '▼'}{' '}
              {formatCurrency(Math.abs(delta), total.currency)} (
              {Math.abs(deltaPct)}%) vs prior period
            </Typography>
          )}
        </Box>
      </Box>

      {/* Daily bar chart */}
      {trend.length >= 2 && (
        <Box
          sx={{
            backgroundColor: getSurfaceColor(theme, 'raised'),
            border: `1px solid ${getSurfaceBorder(theme)}`,
            borderRadius: `${radii.xl}px`,
            p: 3,
          }}
        >
          <Typography
            sx={{
              color: tokens.textPrimary,
              fontSize: 14,
              fontWeight: 700,
              mb: 2,
            }}
          >
            Daily spend
          </Typography>
          <DailyBarChart
            points={trend}
            color={accentColor}
            currency={total.currency}
          />
        </Box>
      )}

      {/* Breakdown table */}
      {breakdown.length > 0 && breakdownColumns.length > 0 && (
        <Box
          sx={{
            backgroundColor: getSurfaceColor(theme, 'raised'),
            border: `1px solid ${getSurfaceBorder(theme)}`,
            borderRadius: `${radii.xl}px`,
            p: 3,
          }}
        >
          <Typography
            sx={{
              color: tokens.textPrimary,
              fontSize: 14,
              fontWeight: 700,
              mb: 2,
            }}
          >
            Cost breakdown
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                {breakdownColumns.map(col => (
                  <TableCell
                    key={col.key}
                    sx={{
                      color: tokens.textMuted,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      borderBottom: `1px solid ${getSurfaceBorder(theme)}`,
                      ...(col.key === 'provider' ? { width: 40, px: 1 } : {}),
                    }}
                  >
                    {col.key === 'provider' ? '' : col.label}
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  sx={{
                    color: tokens.textMuted,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    borderBottom: `1px solid ${getSurfaceBorder(theme)}`,
                  }}
                >
                  Amount
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: tokens.textMuted,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    borderBottom: `1px solid ${getSurfaceBorder(theme)}`,
                  }}
                >
                  vs prior
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: tokens.textMuted,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    borderBottom: `1px solid ${getSurfaceBorder(theme)}`,
                  }}
                >
                  Share
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {breakdown.map((item, rowIdx) => (
                <TableRow
                  // eslint-disable-next-line react/no-array-index-key
                  key={rowIdx}
                  sx={{ '&:last-child td': { border: 0 } }}
                >
                  {breakdownColumns.map(col => {
                    const value = item.dimensions[col.key] || '—';
                    const isProvider = col.key === 'provider';
                    return (
                      <TableCell
                        key={col.key}
                        sx={{
                          color: tokens.textPrimary,
                          fontSize: 12,
                          borderBottom: `1px solid ${getSurfaceBorder(theme)}`,
                          ...(isProvider
                            ? { width: 40, px: 1 }
                            : {
                                maxWidth: 160,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }),
                        }}
                      >
                        {isProvider ? (
                          value !== '—' ? (
                            <Tooltip title={value} placement="right">
                              <Box sx={{ display: 'inline-flex' }}>
                                <ProviderIcon provider={value} size={20} />
                              </Box>
                            </Tooltip>
                          ) : (
                            '—'
                          )
                        ) : (
                          value
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      color: tokens.textPrimary,
                      fontSize: 12,
                      fontWeight: 600,
                      borderBottom: `1px solid ${getSurfaceBorder(theme)}`,
                    }}
                  >
                    {formatCurrency(item.amount, total.currency)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      borderBottom: `1px solid ${getSurfaceBorder(theme)}`,
                      whiteSpace: 'nowrap',
                      color: (() => {
                        if (item.previousAmount === undefined)
                          return tokens.textMuted;
                        const d = item.amount - item.previousAmount;
                        if (Math.abs(d) < 0.005) return tokens.textMuted;
                        return d > 0
                          ? theme.palette.error.main
                          : theme.palette.success.main;
                      })(),
                    }}
                  >
                    {item.previousAmount === undefined ? (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          color: tokens.textMuted,
                          border: `1px solid currentColor`,
                          borderRadius: '3px',
                          px: 0.5,
                          py: 0.25,
                        }}
                      >
                        New
                      </Typography>
                    ) : (
                      (() => {
                        const d = item.amount - item.previousAmount;
                        const pct =
                          item.previousAmount > 0
                            ? Math.round((d / item.previousAmount) * 100)
                            : null;
                        if (Math.abs(d) < 0.005) return '—';
                        return `${d > 0 ? '▲' : '▼'} ${
                          pct !== null
                            ? `${Math.abs(pct)}%`
                            : formatCurrency(Math.abs(d), total.currency)
                        }`;
                      })()
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: tokens.textMuted,
                      fontSize: 12,
                      borderBottom: `1px solid ${getSurfaceBorder(theme)}`,
                    }}
                  >
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: alpha(accentColor, 0.15),
                          borderRadius: '999px',
                          height: 4,
                          overflow: 'hidden',
                          width: 32,
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: accentColor,
                            borderRadius: '999px',
                            height: '100%',
                            width: `${item.percentage}%`,
                          }}
                        />
                      </Box>
                      {item.percentage}%
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};
